import React, { useMemo } from 'react'
import { Col, Row } from 'react-bootstrap'
import { SelectDropdown, SelectOption } from '../SelectDropdown/SelectDropdown'
import { Button } from '../Button/Button'
import { Tooltip } from '../Tooltip/Tooltip'

export interface ConditionRowProps {
  selectedLeftValue: string | null
  selectedOperator: string | null
  isEnforced?: boolean
  loading?: boolean

  leftValueOptions: SelectOption[]
  operatorOptions: SelectOption[]
  leftValuePlaceholder?: string
  operatorPlaceholder?: string

  rightValueComponent: React.ReactNode

  onLeftValueChange: (typeId: string) => void
  onOperatorChange: (operatorId: string) => void
  onRemove: () => void
}

export const ConditionRow: React.FC<ConditionRowProps> = ({
  selectedLeftValue,
  selectedOperator,
  isEnforced = false,
  loading = false,
  leftValueOptions,
  operatorOptions,
  leftValuePlaceholder = 'Select field',
  operatorPlaceholder = 'Select operator',
  rightValueComponent,
  onLeftValueChange,
  onOperatorChange,
  onRemove,
}) => {
  const initialLeftValueOption = useMemo(() => {
    if (!selectedLeftValue) {
      return null
    }
    return leftValueOptions.find((o) => o.id === selectedLeftValue) ?? { id: selectedLeftValue, label: selectedLeftValue }
  }, [leftValueOptions, selectedLeftValue])
  const initialOperatorOption = useMemo(() => {
    if (!selectedOperator) {
      return null
    }
    return operatorOptions.find((o) => o.id === selectedOperator) ?? { id: selectedOperator, label: selectedOperator }
  }, [operatorOptions, selectedOperator])

  return (
    <Row className='g-2 align-items-center'>
      <Col xs={12} md>
        <SelectDropdown
          searchable
          initialSelectedOption={initialLeftValueOption}
          options={leftValueOptions}
          className='py-2 zocks-px-0.75'
          onOptionSelected={(option) => onLeftValueChange(option.id)}
          placeholder={leftValuePlaceholder}
          disabled={isEnforced}
          loading={loading}
        />
      </Col>

      <Col xs={12} md>
        <SelectDropdown
          initialSelectedOption={initialOperatorOption}
          options={operatorOptions}
          className='py-2 zocks-px-0.75'
          onOptionSelected={(option) => onOperatorChange(option.id)}
          placeholder={operatorPlaceholder}
          disabled={isEnforced}
        />
      </Col>

      <Col xs={12} md>
        {rightValueComponent}
      </Col>

      <Col xs='auto'>
        <div className='d-flex align-items-center gap-2'>
          <Button variant='primaryGhost' className='p-2' onClick={onRemove} disabled={isEnforced}>
            <i className='bi bi-trash' />
          </Button>

          {isEnforced ? (
            <Tooltip className='zocks-text-gray-500' placement='right' title='This condition is enforced by your organization and cannot be changed'>
              <i className='bi bi-info-circle' />
            </Tooltip>
          ) : (
            <span className='invisible'>
              <i className='bi bi-info-circle' />
            </span>
          )}
        </div>
      </Col>
    </Row>
  )
}
